import { every, map } from 'underscore';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Tab, TabList } from '@fluentui/react-components';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  MessageBar,
  MessageBarBody,
  MessageBarTitle
} from '@fluentui/react-message-bar';

import { withSidePanel } from './with-side-panel';
import { ActionBar } from './action-bar';
import { getSpecificTargetStyles } from './styles/specific-target-style';
import { ExactUrlTarget } from './exact-url-target';
import { RuleTarget } from './rule-target';
import { useDefaultBid } from '../utils';

const TABLIST = {
  EXACTURLS: 'exactUrls',
  RULES: 'rules',
};

const SpecificTarget = ({
  data, onDataChange, onCancel, preloadedCategories, domainName, languageCode, selectedTargetingSource,
}) => {
  const { exactUrls, rules } = data;

  const i18n = useContext(I18nContext);
  const classes = useDefaultStyles(getSpecificTargetStyles);
  const activity = useContext(ActivityContext);
  const defaultBid = useDefaultBid();

  const [exactUrlItemKeys, setExactUrlItemKeys] = useState({});
  const [ruleItemKeys, setRuleItemKeys] = useState({});

  const [selectedTab, setSelectedTab] = useState(TABLIST.EXACTURLS);

  const [applyFailCount, setApplyFailCount] = useState(0);
  const [showExactUrlsTabError, setShowExactUrlsTabError] = useState(false);
  const [showRulesTabError, setShowRulesTabError] = useState(false);

  useEffect(() => {
    onDataChange({
      exactUrls: {
        ...exactUrls,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        bufferItems: exactUrls.items.map(({ errors, ...rest }) => ({ ...rest })),
      },
      rules: {
        ...rules,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        bufferItems: rules.items.map(({ errors, ...rest }) => ({ ...rest })),
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExactUrlDataChange = useCallback((newData) => {
    onDataChange({ exactUrls: { ...exactUrls, ...newData } });
    setShowExactUrlsTabError(false);
  }, [exactUrls, onDataChange]);

  const onRuleDataChange = useCallback((newData) => {
    onDataChange({ rules: { ...rules, ...newData } });
    setShowRulesTabError(false);
  }, [onDataChange, rules]);

  const verifyData = useCallback(() => {
    const isExactUrlsValid = every(Object.values(exactUrlItemKeys), value => value <= 1) && every(exactUrls.bufferItems, item => item.isValid);
    const isRulesValid = every(Object.values(ruleItemKeys), value => value <= 1) && every(rules.bufferItems, item => item.isValid);
    setShowExactUrlsTabError(!isExactUrlsValid);
    setShowRulesTabError(!isRulesValid);
    return isExactUrlsValid && isRulesValid;
  }, [exactUrlItemKeys, exactUrls.bufferItems, ruleItemKeys, rules.bufferItems]);

  const onTabSelect = useCallback((_e, tabData) => {
    setSelectedTab(tabData.value);
    activity.signal(`onSwitchSpecificTargetSidePanelTab - ${JSON.stringify({
      Tab: tabData.value,
    })}`);
  }, [activity]);

  const onApply = useCallback(() => {
    if (!verifyData()) {
      setApplyFailCount(count => count + 1);
      return;
    }
    const newExactItems = map(exactUrls.bufferItems, item => ({
      ...item,
      Bid: item.Bid || defaultBid,
    }));
    const newRuleItems = map(rules.bufferItems, item => ({
      ...item,
      Bid: item.Bid || defaultBid,
    }));
    onDataChange({ exactUrls: { ...exactUrls, items: newExactItems }, rules: { ...rules, items: newRuleItems } });
    onCancel();
  }, [exactUrls, onCancel, onDataChange, rules, verifyData, defaultBid]);

  return (
    <div className="specific-target-panel-container">
      <div className={classnames(classes.tabRoot, 'tab-container')}>
        <MessageBar className="message-bar">
          <MessageBarBody>
            <MessageBarTitle>{i18n.getString(_TL_('Note'))}</MessageBarTitle>
            <span>
              {selectedTab === TABLIST.EXACTURLS ?
                i18n.getString(_TL_('You can target exact URLs on your website.'))
                : i18n.getString(_TL_('You can target specific pages of your website based on their URL, category, page title, page content, or custom label.'))
              }
            </span>
          </MessageBarBody>
        </MessageBar>
        <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
          <Tab value={TABLIST.EXACTURLS}>
            <span>{i18n.getString(_TL_('Exact URLs'))}</span>
            {showExactUrlsTabError && <span className={classes.tabError}>*</span>}
          </Tab>
          <Tab value={TABLIST.RULES}>
            <span>{i18n.getString(_TL_('Rules'))}</span>
            {showRulesTabError && <span className={classes.tabError}>*</span>}
          </Tab>
        </TabList>
      </div>
      {
        selectedTab === TABLIST.EXACTURLS && (
          <ExactUrlTarget
            data={exactUrls}
            onDataChange={onExactUrlDataChange}
            itemKeys={exactUrlItemKeys}
            setItemKeys={setExactUrlItemKeys}
            applyFailCount={applyFailCount}
          />
        )
      }
      {
        selectedTab === TABLIST.RULES && (
          <RuleTarget
            data={rules}
            onDataChange={onRuleDataChange}
            itemKeys={ruleItemKeys}
            setItemKeys={setRuleItemKeys}
            applyFailCount={applyFailCount}
            preloadedCategories={preloadedCategories}
            domainName={domainName}
            languageCode={languageCode}
            selectedTargetingSource={selectedTargetingSource}
          />
        )
      }
      <ActionBar onApply={onApply} onCancel={onCancel} />
    </div>
  );
};

SpecificTarget.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape({
    exactUrls: PropTypes.shape({
      items: PropTypes.arrayOf(PropTypes.shape({})),
      bufferItems: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  })).isRequired,
  onDataChange: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  preloadedCategories: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  domainName: PropTypes.string.isRequired,
  languageCode: PropTypes.string.isRequired,
  selectedTargetingSource: PropTypes.string.isRequired,
};

export const SpecificTargetPanel = withSidePanel(SpecificTarget);
